import React from 'react'
import draft from 'draft-js'

import data from './data'
import model from './model'
import './App.css'

const SaveState = {
  NEW: 'new',
  SAVING: 'saving',
  SAVED: 'saved',
}

function getNoteIdFromPath() {
  return window.location.pathname.split('/')[1] || ''
}

class App extends React.Component {
  constructor() {
    super()
    this.state = {
      editorState: draft.EditorState.createEmpty(),
      note: null,
      saveState: SaveState.NEW,
    }
  }

  componentDidMount() {
    const noteId = getNoteIdFromPath()
    if (!noteId) {
      this.refs.editor.focus()
      return
    }

    data.getNote(noteId).then((note) => {
      // Create a Note for the URL provided, but wait for content to persist it
      if (!note) {
        const {editorState} = this.state
        const now = Date.now()
        this.setState({
          note: new model.Note({
            id: noteId,
            content: editorState.getCurrentContent(),
            createdAt: now,
            updatedAt: now,
          }),
        })
        this.refs.editor.focus()
        return
      }

      const editorState = draft.EditorState.createWithContent(note.content)

      this.setState({
        saveState: SaveState.SAVED,
        note,
        editorState,
      })

      this.refs.editor.focus()
    })
  }

  onEditorStateChange(newEditorState: draft.EditorState) {
    const {note, saveState, editorState} = this.state
    const currentContent = newEditorState.getCurrentContent()
    const contentChanged = editorState.getCurrentContent() !== currentContent

    this.setState({
      saveState: contentChanged ? SaveState.SAVING : saveState,
      // EditorState must always be updated quickly and synchronously, for
      // interaction reasons.
      editorState: newEditorState,
    })
    if (!contentChanged) return

    const isNew = !note
    const notePromise = isNew ?
        this._createNote(currentContent) :
        this._updateNote(note, currentContent)

    notePromise.then((newNote) => {
      this.setState({
        saveState: SaveState.SAVED,
        note: newNote,
      })
      if (isNew) {
        window.history.pushState({noteId: newNote.id}, '', `/${newNote.id}`)
      }
    })
  }

  _createNote(currentContent: draft.ContentState): Promise<model.Note> {
    const now = Date.now()
    const newNote = new model.Note({
      id: model.Note.generateId(),
      content: currentContent,
      createdAt: now,
      updatedAt: now,
    })
    return data.upsertNote(newNote)
  }

  _updateNote(note: model.Note, currentContent: draft.ContentState): Promise<model.Note> {
    const newNote = note
      .set('updatedAt', Date.now())
      .set('content', currentContent)
    return data.upsertNote(newNote)
  }

  handleBeforeInput(chars: string): draft.DraftHandleValue {
    const {editorState} = this.state
    const content = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const anchorKey = selection.getAnchorKey()
    const block = content.getBlockForKey(anchorKey)
    const start = selection.getStartOffset()
    const textBefore = block.getText().slice(0, start)

    if (chars === `'` || chars === '"') {
      let isSingle = chars === `'`
      if (textBefore === '' || textBefore.endsWith(' ')) {
        this._replaceText(isSingle ? '‘' : '“')
      } else {
        this._replaceText(isSingle ? '’' : '”')
      }
      return 'handled'
    }

    return 'not-handled'
  }

  _replaceText(text: string) {
    const {editorState} = this.state
    const content = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const newContent = draft.Modifier.replaceText(content, selection, text)
    const newEditorState = draft.EditorState.push(
      editorState,
      newContent,
      'insert-characters'
    )
    this.onEditorStateChange(newEditorState)
  }

  render() {
    const {editorState, note, saveState} = this.state
    const timestamp = note && new Date(note.updatedAt)
    return (
      <div className="App">
        <div className="App-editor">
          <draft.Editor
            editorState={editorState}
            handleBeforeInput={this.handleBeforeInput.bind(this)}
            onChange={this.onEditorStateChange.bind(this)}
            // TODO figure out why this is formatting weird
            // placeholder="Start writing…"
            ref="editor"
          />
        </div>
        <Metabar saveState={saveState} timestamp={timestamp}/>
      </div>
    )
  }
}

const Metabar = (props: Object) => {
  const {saveState, timestamp} = props
  const statusText =
      saveState === SaveState.SAVED ? 'Saved' :
      saveState === SaveState.SAVING ? 'Saving…' :
      'New'
  let timeField = null
  if (timestamp) {
    timeField = (
      <div className="Metabar-time">
        Last updated{' '}
        <time title="{timestamp.toISOString()}">
          {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
        </time>
      </div>
    )
  }

  return (
    <div className="Metabar">
      <div className="Metabar-inner">
        {timeField}
        <div className="Metabar-state">{statusText}</div>
      </div>
    </div>
  )
}

export default App



/** WEBPACK FOOTER **
 ** ./src/App.js
 **/